import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MEMBER_STATUS, shortDate, type Member, type MemberStatus } from '@lcc/shared';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDebounced } from '../lib/hooks';
import {
  Card, Empty, ErrorNote, Loading, Modal, SelectField, Status, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';

export function MembersPage() {
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<MemberStatus | ''>('active');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const q = useDebounced(search);

  const members = useQuery({
    queryKey: ['members', q, status, page],
    queryFn: () => api.get<{ data: Member[]; total: number; totalPages: number }>(
      `/members${qs({ q, status: status || undefined, page, perPage: 50 })}`,
    ),
  });

  return (
    <>
      <PageHeader
        title="Members"
        sub={members.data ? `${members.data.total} on the roster` : undefined}
        actions={isAdmin && (
          <button className="btn btn--accent" type="button" onClick={() => setAdding(true)}>
            Add member
          </button>
        )}
      />

      <div className="content stack">
        <Card>
          <div className="row">
            <div style={{ flex: '1 1 240px' }}>
              <TextField label="Search" value={search}
                onChange={(v) => { setSearch(v); setPage(1); }}
                placeholder="Name or email" />
            </div>
            <div style={{ width: 180 }}>
              <SelectField
                label="Status" value={status}
                onChange={(v) => { setStatus(v); setPage(1); }}
                options={[
                  { value: '' as const, label: 'All statuses' },
                  ...MEMBER_STATUS.map((s) => ({ value: s, label: s })),
                ]}
              />
            </div>
          </div>
        </Card>

        <Card flush>
          {members.isLoading ? <Loading what="Loading members" />
            : members.error ? <div style={{ padding: '1rem' }}><ErrorNote error={members.error} /></div>
            : members.data && members.data.data.length === 0
              ? <Empty title="No members match">Try a different search or status.</Empty>
              : (
                <>
                  <div className="table-wrap">
                    <table className="t">
                      <thead>
                        <tr>
                          <th>Name</th><th>Email</th><th>Phone</th>
                          <th>Status</th><th>Roles</th><th>Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.data?.data.map((m) => (
                          <tr key={m.id}>
                            <td><Link to={`/members/${m.id}`}>{m.fullName}</Link>
                              {m.isLifeMember && <> <span className="pill pill--good">life</span></>}
                            </td>
                            <td className="muted small">{m.email ?? '—'}</td>
                            <td className="muted small nowrap">{m.phone ?? '—'}</td>
                            <td><Status value={m.status} /></td>
                            <td className="row row--tight">
                              {m.roles.filter((r) => r !== 'member').map((r) => (
                                <span className="pill" key={r}>{r}</span>
                              ))}
                            </td>
                            <td className="muted small nowrap">{m.joinedOn ? shortDate(m.joinedOn) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {members.data && members.data.totalPages > 1 && (
                    <div className="row" style={{ padding: '.75rem 1rem', justifyContent: 'center' }}>
                      <button className="btn btn--sm" type="button" disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}>Previous</button>
                      <span className="small muted">Page {page} of {members.data.totalPages}</span>
                      <button className="btn btn--sm" type="button"
                        disabled={page >= members.data.totalPages}
                        onClick={() => setPage((p) => p + 1)}>Next</button>
                    </div>
                  )}
                </>
              )}
        </Card>
      </div>

      {adding && <AddMemberModal onClose={() => setAdding(false)} />}
    </>
  );
}

function AddMemberModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', joinedOn: '',
  });

  const create = useMutation({
    mutationFn: () => api.post<{ data: Member }>('/members', {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email || null,
      phone: form.phone || null,
      joinedOn: form.joinedOn || null,
      status: 'active',
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['members'] });
      toast.success(`${r.data.fullName} added.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const fieldErrors = (create.error as { fieldErrors?: Record<string, string[]> })?.fieldErrors ?? {};

  return (
    <Modal
      title="Add a member"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn--accent" type="button"
            disabled={!form.firstName || !form.lastName || create.isPending}
            onClick={() => create.mutate()}>
            {create.isPending ? 'Adding…' : 'Add member'}
          </button>
        </>
      }
    >
      <fieldset disabled={create.isPending}>
        {create.error ? <ErrorNote error={create.error} /> : null}
        <div className="grid grid--2">
          <TextField label="First name" value={form.firstName} onChange={set('firstName')}
            required error={fieldErrors.firstName} />
          <TextField label="Last name" value={form.lastName} onChange={set('lastName')}
            required error={fieldErrors.lastName} />
        </div>
        <TextField label="Email" type="email" value={form.email} onChange={set('email')}
          error={fieldErrors.email}
          hint="Needed before they can be given a portal login." />
        <div className="grid grid--2">
          <TextField label="Phone" value={form.phone} onChange={set('phone')} error={fieldErrors.phone} />
          <TextField label="Joined on" type="date" value={form.joinedOn} onChange={set('joinedOn')} />
        </div>
        <div className="banner banner--info">
          New members must sign their waivers before they can be added to a team.
        </div>
      </fieldset>
    </Modal>
  );
}
